import { Link, NavLink } from "react-router-dom";
import { useCart } from "../hooks/use-cart";
import { AuthNav } from "./auth-nav";
import { SearchCommand } from "./search-command";
import { ThemeToggle } from "./theme-toggle";
import { CartIcon } from "./site-icons";

const primaryLinks = [
  { label: "Shop", to: "/shop" },
  { label: "Projects", to: "/projects" },
  { label: "Search", to: "/shop/search" }
];

function PhoneIcon() {
  return (
    <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path d="M21 16.2v2.7a2.1 2.1 0 0 1-2.3 2.1A18 18 0 0 1 3 5.3 2.1 2.1 0 0 1 5.1 3H7.8a2 2 0 0 1 2 1.7l.5 3a2 2 0 0 1-.6 1.8l-1.5 1.5a14.5 14.5 0 0 0 5.8 5.8l1.5-1.5a2 2 0 0 1 1.8-.6l3 .5a2 2 0 0 1 1.7 2Z" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 6.5h16a1.5 1.5 0 0 1 1.5 1.5v8A1.5 1.5 0 0 1 20 17.5H4A1.5 1.5 0 0 1 2.5 16V8A1.5 1.5 0 0 1 4 6.5Z" />
      <path d="m3 7 9 7 9-7" />
    </svg>
  );
}

export function Header() {
  const { cart } = useCart();
  const cartQuantity = cart?.summary.quantity || 0;
  const navLinks = cartQuantity > 0 ? [...primaryLinks, { label: "Checkout", to: "/shop/checkout" }] : primaryLinks;

  return (
    <header className="site-header sticky top-0 z-30 backdrop-blur-xl">
      <div className="site-header__utility hidden sm:block">
        <div className="section-shell flex flex-col gap-3 py-2.5 text-xs sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <a className="site-header__contact" href="tel:+254795952128">
              <PhoneIcon />
              <span>+254 795 952 128</span>
            </a>
            <a className="site-header__contact" href="mailto:info@novatch.co.ke">
              <MailIcon />
              <span>info@novatch.co.ke</span>
            </a>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-nv-slate">
            <div className="hidden items-center gap-2 sm:flex">
              <span className="site-header__utility-note">Electronics commerce</span>
              <span className="site-header__utility-note hidden lg:inline-block">Technical sourcing</span>
            </div>
            <AuthNav className="site-header__contact uppercase" />
          </div>
        </div>
      </div>

      <div className="section-shell py-4">
        <div className="grid gap-4 xl:grid-cols-[auto_minmax(0,1fr)_auto] xl:items-center">
          <div className="flex items-center justify-between gap-4">
            <Link className="flex min-w-0 items-center gap-3" to="/">
              <img alt="Novatech Logo" className="h-12 w-12 shrink-0 rounded-2xl object-contain" src="/logo/logo.jpg" />
              <div className="min-w-0">
                <p className="font-display text-xl font-bold tracking-tight text-nv-ink">Novatech</p>
                <p className="hidden truncate text-sm text-nv-slate sm:block">Innovate, Enhance and Sustain</p>
              </div>
            </Link>

            <div className="flex items-center gap-2 xl:hidden">
              <ThemeToggle />
            </div>
          </div>

          <div className="min-w-0">
            <SearchCommand />
          </div>

          <div className="hidden items-center justify-end gap-3 xl:flex">
            <Link aria-label="View Cart" className="site-header__cart-link" to="/shop/cart">
              <CartIcon />
              {cartQuantity > 0 ? <span className="site-header__cart-badge">{cartQuantity}</span> : null}
            </Link>
            <ThemeToggle />
          </div>
        </div>

        <div className="mt-4 hidden flex-col gap-3 md:flex xl:flex-row xl:items-center xl:justify-between">
          <nav className="site-header__nav-scroll flex items-center gap-2 overflow-x-auto pb-1">
            {navLinks.map((link) => (
              <NavLink
                className={({ isActive }) => `site-header__nav-link${isActive ? " site-header__nav-link--active" : ""}`}
                end={link.to === "/shop"}
                key={link.to}
                to={link.to}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-3 xl:flex">
            <span className="site-header__support-copy">Call or email for product sourcing, custom builds, and order support.</span>
          </div>
        </div>
      </div>
    </header>
  );
}
